import { Link } from 'react-router-dom'
import { useStyletron } from 'styletron-react'
import * as styles from './styles'
import { useEffect, useMemo, useState } from 'react'
import { Card, Flexbox, Heading, Icon } from '@components/common'
import { Text } from '@components/common'
import { motion as m, AnimatePresence } from 'framer-motion'
import { useDepreciationStatus } from '@components/layout/Frame/TopBar/DepreciationStatus/useDepreciationStatus'

const ReportMenuNavLink = ({
  to,
  title,
  description,
  keyValue,
  showMenu,
  setShowMenu,
}: {
  to: string
  title: string
  description: string
  keyValue: number
  showMenu: Boolean
  setShowMenu: Function
}) => {
  const [css] = useStyletron()
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipTimer, setTooltipTimer] = useState<NodeJS.Timeout | null>(null)

  const { depRunStatus } = useDepreciationStatus()

  const { IsRunningDepreciation } = depRunStatus || {}

  const isDisabled = useMemo(() => {
    if (title === 'Depreciation Edit' && IsRunningDepreciation === false) {
      return true
    }
    if (title === 'G/L Posting Preview' && IsRunningDepreciation === false) {
      return true
    }
    return false
  }, [title, IsRunningDepreciation])

  const toggleShowMenu = () => {
    setShowMenu(!showMenu)
  }

  const handleClick = (e: React.MouseEvent) => {
    if (isDisabled) {
      e.preventDefault()
      return
    }
    toggleShowMenu()
  }

  const getTooltipContent = () => {
    if (isDisabled) {
      return 'This report is unavailable without a pending depreciation run.'
    }
    return description
  }

  const handleMouseEnter = () => {
    const timer = setTimeout(() => {
      setShowTooltip(true)
    }, 1000)
    setTooltipTimer(timer)
  }

  const handleMouseLeave = () => {
    if (tooltipTimer) {
      clearTimeout(tooltipTimer)
      setTooltipTimer(null)
    }
    setShowTooltip(false)
  }

  // Clear any pending tooltip timers on unmount to avoid state updates on unmounted component
  useEffect(() => {
    return () => {
      if (tooltipTimer) {
        clearTimeout(tooltipTimer)
      }
    }
  }, [tooltipTimer])

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Link to={to} onClick={handleClick}>
        <Card key={keyValue} className={css(styles.card(isDisabled))} onClick={handleClick}>
          <Flexbox
            justifyContent="center"
            alignItems="center"
            gap={5}
            flexDirection="column"
          >
            <Icon name="FileText" className={css(styles.icon(isDisabled))} />
            <Flexbox
              flexDirection="column"
              gap={2}
              alignItems="center"
              justifyContent="center"
            >
              <Heading type="h4" style={{ textAlign: 'center' }}>
                {title}
              </Heading>
            </Flexbox>
          </Flexbox>
        </Card>
      </Link>

      <AnimatePresence>
        {showTooltip && (
          <m.div
            className={css(styles.shortDescriptionTip)}
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.15 }}
          >
            <Text>{getTooltipContent()}</Text>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ReportMenuNavLink
